/**
 * Static legal pages - Privacy + Terms.
 *
 * BUG-2026-05-05-02: render single-locale via useLang(). EN body not
 * yet translated by legal-upl agent - render an explicit fallback
 * notice + the VN authoritative version (so the user still sees the
 * actual binding text, with disclosure that VN is canonical).
 */

import { Link } from 'react-router-dom';
import { useLang } from '../use-lang';

const PRIVACY_VN = `Asklaw bảo vệ dữ liệu của bạn theo Nghị định 13/2023/NĐ-CP và Luật Bảo vệ dữ liệu cá nhân 2026.

Dữ liệu thu thập:
- Tài khoản: email + tên hiển thị (Google OAuth).
- Sử dụng: câu hỏi, lịch sử Ask, lịch sử Shortcut.
- Kỹ thuật: cookie thiết yếu (đăng nhập), analytics nếu bạn đồng ý.

Mục đích sử dụng:
- Cung cấp dịch vụ Ask/Shortcut/Doc Pack.
- Cải thiện chất lượng citation và refusal.
- Phòng chống lạm dụng.

Bạn có quyền truy cập, sửa, xóa dữ liệu cá nhân - liên hệ DPO: dpo@asklaw.vn.

Asklaw KHÔNG bán dữ liệu cá nhân. KHÔNG share với bên thứ ba ngoài infra cung cấp dịch vụ (Cloudflare, Google OAuth).`;

const TERMS_VN = `Asklaw là công cụ AI tham khảo thông tin pháp luật, KHÔNG phải dịch vụ tư vấn pháp lý.

Khi sử dụng Asklaw, bạn đồng ý:
1. Mọi output do AI tạo, có thể sai sót - luôn xác minh bằng Điều/Khoản gốc.
2. Asklaw không chịu trách nhiệm pháp lý cho quyết định dựa trên output AI.
3. Để được tư vấn chính thức cho vụ việc cụ thể, vui lòng liên hệ luật sư có chứng chỉ hành nghề tại Việt Nam.
4. Không dùng Asklaw để thực hiện hành vi vi phạm pháp luật.
5. Không tự động hoá truy vấn (rate limit per account).

Asklaw có quyền tạm khoá tài khoản vi phạm. Hợp đồng có hiệu lực kể từ khi bạn truy cập trang web hoặc đăng ký tài khoản.`;

export function PrivacyRoute() {
  return <LegalLayout titleKey="privacy" body={PRIVACY_VN} />;
}

export function TermsRoute() {
  return <LegalLayout titleKey="terms" body={TERMS_VN} />;
}

function LegalLayout({
  titleKey,
  body,
}: {
  titleKey: 'privacy' | 'terms';
  body: string;
}) {
  const { lang } = useLang();

  const titles = {
    privacy: { vi: 'Chính sách bảo mật', en: 'Privacy Policy' },
    terms: { vi: 'Điều khoản sử dụng', en: 'Terms of Service' },
  };
  const title = titles[titleKey][lang];

  const t = {
    home: lang === 'vi' ? '<- Trang chủ' : '<- Home',
    updated: lang === 'vi' ? 'Cập nhật' : 'Updated',
    fallbackNotice: 'English version coming soon. Please refer to the Vietnamese version below - it is the binding canonical text.',
  };

  return (
    <main style={{ maxWidth: 720, margin: '48px auto 96px', padding: '0 16px' }}>
      <p style={{ marginBottom: 8 }}>
        <Link to="/" style={{ color: 'var(--color-muted)', fontSize: 14 }}>
          {t.home}
        </Link>
      </p>
      <h1 style={{ marginTop: 0 }}>{title}</h1>

      {lang === 'en' ? (
        <div
          role="note"
          className="banner"
          style={{ marginTop: 8, marginBottom: 16, fontSize: 14 }}
        >
          {t.fallbackNotice}
        </div>
      ) : null}

      <article
        style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: 15 }}
        lang="vi"
      >
        {body}
      </article>
      <p style={{ marginTop: 32, fontSize: 13, color: 'var(--color-muted)' }}>
        {t.updated}: 2026-05-05. DPO: <a href="mailto:dpo@asklaw.vn">dpo@asklaw.vn</a>
      </p>
    </main>
  );
}
